import { useState } from "react";
import { Dialog } from "@mui/material";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import { Box, HStack, VStack } from "../../components/utils";
import {
  DeleteCompanyResult,
  removeTaskManagerCompany,
} from "../../api/companyExcelOnboarding";
import { queryClient } from "../../queries";

type Props = {
  open: boolean;
  companies: { uuid: string; nameOfTheCompany: string }[];
  onClose: () => void;
};

function getErrorMessage(error: any) {
  const response = error?.response?.data;
  return (
    response?.errorMessage ||
    response?.message ||
    response?.errors?.[0] ||
    error?.message ||
    "Database deletion failure"
  );
}

const formatDate = (value?: string | null) =>
  value
    ? new Date(value).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

export default function DeleteCompanyModal({
  open,
  companies,
  onClose,
}: Props) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [projects, setProjects] = useState<
    NonNullable<DeleteCompanyResult["projects"]>
  >([]);
  const [showTaskWarning, setShowTaskWarning] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");
  const [blockedCompanyNames, setBlockedCompanyNames] = useState<string[]>([]);

  const handleClose = () => {
    if (!isDeleting) {
      setProjects([]);
      setBlockedCompanyNames([]);
      setShowTaskWarning(false);
      setWarningMessage("");
      onClose();
    }
  };

  const handleDelete = async () => {
    const companyIds = companies.map(({ uuid }) => uuid).filter(Boolean);
    if (!companyIds.length) {
      toast.error("Missing company ID");
      return;
    }

    setIsDeleting(true);
    try {
      const response = await removeTaskManagerCompany({
        companyIds,
      });
      const result = response;
      const deletedCompanyIds = result?.deletedCompanyIds || [];
      const deletedIds = new Set(deletedCompanyIds);
      const deletedNames = companies
        .filter((company) => deletedCompanyIds.includes(company.uuid))
        .map((company) => company.nameOfTheCompany);
      if (deletedCompanyIds.length) {
        queryClient.setQueryData<any[]>("userCompanies", (items) =>
          (items || []).filter((item) => !deletedIds.has(item.uuid))
        );
        toast.success(
          `Deleted ${deletedCompanyIds.length} compan${
            deletedCompanyIds.length === 1 ? "y" : "ies"
          }`
        );
      }
      if (result?.blockedCompanies?.length) {
        const blockedIds = result.blockedCompanies.map(
          (company) => company.companyId
        );
        const blockedDisplayNames = blockedIds.map(
          (blockedId) =>
            companies.find((company) => company.uuid === blockedId)
              ?.nameOfTheCompany || blockedId
        );
        setBlockedCompanyNames(blockedDisplayNames);
        const blockedCompany = result.blockedCompanies[0];
        setProjects(blockedCompany?.projects || result.projects || []);
        toast.error(
          `Could not delete: ${result.blockedCompanies
            .map(
              (company) =>
                companies.find((item) => item.uuid === company.companyId)
                  ?.nameOfTheCompany || company.companyId
            )
            .join(", ")} because they have linked tasks`
        );
        setWarningMessage(
          "Companies with linked Task Manager tasks were not deleted."
        );
        setShowTaskWarning(true);
        return;
      }
      queryClient.setQueryData<any[]>("userCompanies", (companies) =>
        (companies || []).filter((item) => !deletedIds.has(item.uuid))
      );
      onClose();
      queryClient.refetchQueries("userCompanies");
    } catch (error: any) {
      const message = getErrorMessage(error);
      const s3CleanupFailed =
        /s3/i.test(message) && /clean|delet|remov|fail/i.test(message);

      if (s3CleanupFailed) {
        // The company is already gone even though source-file cleanup failed.
        queryClient.setQueryData<any[]>("userCompanies", (companies) =>
          (companies || []).filter((item) => !companyIds.includes(item.uuid))
        );
        toast.warning(message);
        onClose();
        queryClient.refetchQueries("userCompanies");
      } else {
        toast.error(message);
      }
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: "rounded-lg" }}
    >
      <Box className="flex flex-col bg-white">
        <HStack className="border-b border-stone-200 px-6 py-5">
          <h2 className="text-xl font-semibold text-stone-800">
            {showTaskWarning ? "Company cannot be deleted" : "Delete Company"}
          </h2>
        </HStack>

        <VStack className="px-6 py-6">
          <HStack className="items-start gap-3 rounded-lg border border-orange-100 bg-orange-50 p-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white">
              <Icon
                icon="mdi:alert-outline"
                height={23}
                className="text-orange-501"
              />
            </div>
            <VStack className="gap-2 pt-0.5">
              <p className="text-base font-semibold text-stone-800">
                {showTaskWarning
                  ? `${
                      blockedCompanyNames.length
                        ? blockedCompanyNames.join(", ")
                        : "This company"
                    } ${blockedCompanyNames.length > 1 ? "are" : "is"} in use`
                  : `Delete ${companies.length} selected ${
                      companies.length === 1 ? "company" : "companies"
                    }?`}
              </p>
              <p className="max-w-2xl text-sm leading-6 text-stone-500">
                {showTaskWarning
                  ? warningMessage
                  : `Companies with linked Task Manager tasks will not be deleted.`}
              </p>
            </VStack>
          </HStack>

          {showTaskWarning && (
            <VStack className="mt-4 max-h-80 gap-3 overflow-y-auto">
              {projects.map((project) => (
                <VStack
                  key={project.id}
                  className="gap-3 rounded-lg border border-stone-200 bg-white p-4"
                >
                  <HStack className="gap-2 text-sm">
                    <span className="font-medium text-stone-500">
                      Project name:
                    </span>
                    <span className="font-semibold text-stone-800">
                      {project.name}
                    </span>
                  </HStack>
                  {project.tasks?.length ? (
                    <VStack className="divide-y divide-stone-100">
                      {project.tasks.map((task) => (
                        <VStack
                          key={task.id}
                          className="gap-2 py-3 first:pt-0 last:pb-0"
                        >
                          <HStack className="gap-2 text-sm">
                            <span className="font-medium text-stone-500">
                              Task name:
                            </span>
                            <span className="text-stone-800">{task.name}</span>
                          </HStack>
                          <HStack className="flex-wrap gap-x-6 gap-y-1 text-xs text-stone-600">
                            <span>
                              <span className="font-medium">Start date:</span>{" "}
                              {formatDate(task.startDate)}
                            </span>
                            <span>
                              <span className="font-medium">End date:</span>{" "}
                              {formatDate(task.endDate)}
                            </span>
                          </HStack>
                        </VStack>
                      ))}
                    </VStack>
                  ) : (
                    <p className="text-sm text-stone-400">No tasks returned</p>
                  )}
                </VStack>
              ))}
            </VStack>
          )}
        </VStack>

        <HStack className="justify-end gap-3 px-6 pb-6">
          <button
            type="button"
            disabled={isDeleting}
            onClick={handleClose}
            className="rounded border border-orange-501 bg-white px-5 py-2.5 text-sm font-semibold text-orange-501 transition hover:bg-orange-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Close
          </button>
          {!showTaskWarning && (
            <button
              type="button"
              disabled={isDeleting}
              onClick={handleDelete}
              className="flex min-w-[150px] items-center justify-center gap-2 rounded bg-orange-501 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isDeleting && (
                <Icon
                  icon="lucide:loader-2"
                  height={16}
                  className="animate-spin"
                />
              )}
              {isDeleting ? "Deleting…" : "Delete Company"}
            </button>
          )}
        </HStack>
      </Box>
    </Dialog>
  );
}
